export const generateIdempotencyKey = (prefix: string): string => {
  const randomUuid = typeof globalThis !== 'undefined'
    && typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}:${randomUuid}`;
};

export const resolveIdempotencyKey = (providedKey: string | undefined, prefix: string): string => {
  const normalizedKey = typeof providedKey === 'string' ? providedKey.trim() : '';
  return normalizedKey || generateIdempotencyKey(prefix);
};

export const buildIdempotencyHeaders = (idempotencyKey: string): Record<string, string> => ({
  'Idempotency-Key': idempotencyKey,
  'X-Idempotency-Key': idempotencyKey,
});

export type StreamRequestError = Error & {
  status?: number;
  retryable?: boolean;
  retryAfterMs?: number;
  response?: {
    status?: number;
    data?: unknown;
  };
};

const MAX_STREAM_RETRY_AFTER_MS = 5 * 60 * 1000;

// These are durable admission outcomes for the current user action. Retrying
// the same streamed request cannot make capacity appear and can amplify
// billing/quota traffic. Availability/configuration failures intentionally do
// not live here because they can recover between attempts.
const TERMINAL_STREAM_ADMISSION_ERROR_CODES = new Set([
  'billing_insufficient_credits',
  'llm_budget_guard_open',
  'llm_daily_budget_exceeded',
  'resource_quota_exceeded',
  'resource_rate_quota_exceeded',
]);

const UNCERTAIN_IDEMPOTENCY_ERROR_CODES = new Set([
  'idempotency_request_in_progress',
  'idempotency_outcome_uncertain',
  'idempotency_operation_outcome_uncertain',
  'idempotency_legacy_outcome_uncertain',
  'idempotency_backend_unavailable',
]);

const getStructuredStreamErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const responseData = (error as StreamRequestError).response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }
  const payload = responseData as {
    error?: { code?: unknown };
    detail?: { code?: unknown };
    error_code?: unknown;
    code?: unknown;
  };
  const code = payload.error?.code
    ?? payload.detail?.code
    ?? payload.error_code
    ?? payload.code;
  return typeof code === 'string' && code.trim() ? code.trim() : null;
};

export const createStreamRequestError = (
  message: string,
  options: {
    status?: number;
    retryable?: boolean;
    retryAfterMs?: number;
    responseData?: unknown;
  } = {},
): StreamRequestError => {
  const error = new Error(message) as StreamRequestError;
  if (typeof options.status === 'number') {
    error.status = options.status;
  }
  if (typeof options.retryable === 'boolean') {
    error.retryable = options.retryable;
  }
  if (
    typeof options.retryAfterMs === 'number'
    && Number.isFinite(options.retryAfterMs)
    && options.retryAfterMs >= 0
  ) {
    error.retryAfterMs = Math.min(options.retryAfterMs, MAX_STREAM_RETRY_AFTER_MS);
  }
  if (options.responseData !== undefined || typeof options.status === 'number') {
    error.response = {
      status: options.status,
      data: options.responseData,
    };
  }
  return error;
};

export const parseStreamRetryAfterMs = (
  retryAfter: string | null | undefined,
  nowMs = Date.now(),
): number | null => {
  const normalized = typeof retryAfter === 'string' ? retryAfter.trim() : '';
  if (!normalized) {
    return null;
  }

  const seconds = Number(normalized);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(Math.ceil(seconds * 1000), MAX_STREAM_RETRY_AFTER_MS);
  }

  const retryAtMs = Date.parse(normalized);
  if (!Number.isFinite(retryAtMs)) {
    return null;
  }
  return Math.min(Math.max(0, retryAtMs - nowMs), MAX_STREAM_RETRY_AFTER_MS);
};

export const getStreamRetryDelayMs = (
  error: unknown,
  fallbackMs: number,
): number => {
  const retryAfterMs = error && typeof error === 'object'
    ? (error as StreamRequestError).retryAfterMs
    : null;
  if (
    typeof retryAfterMs === 'number'
    && Number.isFinite(retryAfterMs)
    && retryAfterMs >= 0
  ) {
    return Math.min(retryAfterMs, MAX_STREAM_RETRY_AFTER_MS);
  }
  return Math.min(Math.max(0, fallbackMs), MAX_STREAM_RETRY_AFTER_MS);
};

export const isRetryableStreamRequestError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return true;
  }

  const structuredCode = getStructuredStreamErrorCode(error);
  if (structuredCode && TERMINAL_STREAM_ADMISSION_ERROR_CODES.has(structuredCode)) {
    return false;
  }
  if (structuredCode && UNCERTAIN_IDEMPOTENCY_ERROR_CODES.has(structuredCode)) {
    return true;
  }

  const retryable = (error as { retryable?: unknown }).retryable;
  if (typeof retryable === 'boolean') {
    return retryable;
  }

  const directStatus = (error as { status?: unknown }).status;
  const responseStatus = (error as StreamRequestError).response?.status;
  const status = typeof directStatus === 'number' ? directStatus : responseStatus;
  return typeof status === 'number' ? status === 408 || status === 425 || status === 429 || status >= 500 : true;
};
